// <CalendarWeekStackPopover> — T-231 (time-view-week-implementation-
// plan.md §6.5).
//
// Defensive-fallback popover triggered by the "+N more" chip when a
// single day-cell has ≥3 overlapping entries. Lists every entry that
// covers the day. Each row click is a stub for now (T-232 polish can
// wire the bar-detail popover, same shape as the Month variant's).

import { useEffect, useRef } from 'react';
import type { CalendarWeekEntry } from '@pa/shared/time-view';

interface Props {
  /** ISO date of the day-cell that triggered the popover. */
  dayIso: string;
  /** Every entry that intersects `dayIso` (already filtered). */
  entries: CalendarWeekEntry[];
  /** Callback when the popover should close (outside-click + Esc). */
  onClose: () => void;
}

const STATUS_LABEL: Record<CalendarWeekEntry['status'], string> = {
  approved: 'Approved',
  pending: 'Pending',
  declined: 'Declined',
};

export default function CalendarWeekStackPopover({
  dayIso,
  entries,
  onClose,
}: Props) {
  const ref = useRef<HTMLDivElement | null>(null);

  // Outside-click + Esc dismissal. Listeners attach on mount; tear
  // down on unmount so we don't leak across remounts.
  useEffect(() => {
    function onPointerDown(e: PointerEvent) {
      if (!ref.current) return;
      if (e.target instanceof Node && !ref.current.contains(e.target)) {
        onClose();
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={`Entries on ${dayIso}`}
      data-testid="week-stack-popover"
      className="absolute z-20 min-w-[240px] mt-2 bg-white border border-slate-200 rounded-lg shadow-lg p-3"
    >
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
        {dayIso}
      </div>
      <ul className="space-y-1.5">
        {entries.map((entry) => (
          <li key={entry.id}>
            <button
              type="button"
              data-testid={`week-stack-popover-row-${entry.id}`}
              className="w-full flex items-center gap-2 text-left px-2 py-1.5 rounded hover:bg-slate-50"
              onClick={() => {
                // T-232 polish wires the bar-detail popover here.
                // For now, no-op — the parent stays open until outside-
                // click / Esc.
              }}
            >
              <span
                className={`inline-block w-2 h-2 rounded-full ${entry.policyColor}`}
                aria-hidden
              />
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-slate-800 truncate">
                  {entry.policyName}
                </span>
                <span className="block text-xs text-slate-500 truncate">
                  {entry.startDate} – {entry.endDate} · {STATUS_LABEL[entry.status]}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
